import { create } from "zustand";
import NotificationsRepository from "@/core/notifications/repositories/NotificationsRepository";
import type { Notification } from "@/core/notifications/entities/Notification";
import GetNotificationsUseCase from "@/core/notifications/useCases/GetNotificationsUseCase/GetNotificationsUseCase";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import type Exception from "@/core/exceptions/Exception";

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: Exception | null;
  loaded: boolean;

  fetchNotifications: (options?: { force?: boolean }) => Promise<void>;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  resetState: () => void;
}

let notificationsRepository: NotificationsRepository | undefined;

export const setNotificationsRepository = (
  repository: NotificationsRepository,
) => {
  notificationsRepository = repository;
};

const initialState = {
  notifications: [] as Notification[],
  unreadCount: 0,
  loading: false,
  error: null as Exception | null,
  loaded: false,
};

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  ...initialState,

  fetchNotifications: async (options) => {
    if (!notificationsRepository) {
      throw new Error(
        "NotificationsRepository not initialized. Call setNotificationsRepository first.",
      );
    }

    const state = get();
    if (state.loading) {
      return;
    }
    if (state.loaded && !options?.force) {
      return;
    }

    set({ loading: true, error: null });

    try {
      const response = await new GetNotificationsUseCase(
        notificationsRepository,
      ).execute();
      set({
        notifications: response.data,
        unreadCount: response.unreadCount,
        loading: false,
        loaded: true,
        error: null,
      });
    } catch (error) {
      set({
        loading: false,
        error: (error as Exception) ?? {
          type: DefaultExceptionTypes.UNKNOWN_ERROR,
          message: "Failed to load notifications",
        },
      });
    }
  },

  markAsRead: (id: string) => {
    set((state) => {
      const notifications = state.notifications.map((n) =>
        n.id === id ? { ...n, read: true } : n,
      );
      return {
        notifications,
        unreadCount: notifications.filter((n) => !n.read).length,
      };
    });
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  },

  resetState: () => {
    set(initialState);
  },
}));
